/**
 * The content model: what a map, a recipe book and a timings file *are*, and
 * what makes one valid.
 *
 * Map layout, recipes, timings and scoring are data. Nothing
 * in the engine reads a hard-coded number; it reads what this module describes.
 *
 * Nothing here touches the filesystem. Reading the files is `loadContent.ts`'s
 * job and belongs to the server; parsing, deriving and validating what was read
 * is pure, which is what lets the client run the same engine over a kitchen it
 * holds as a literal (the tutorial sandbox).
 *
 * Validation is loud and eager: the process fails at boot rather than halfway
 * through a round, and `/api/ready` reports the same three checks.
 */

import type {
  Cell,
  ContainerKind,
  DishRequirement,
  EquipmentId,
  IngredientId,
  IngredientSummary,
  LatencyConfig,
  RequiredItem,
  ScheduledOrder,
  StationType,
} from '@overcooked/shared'
import { DIRECTION_VECTORS, TICK_MS, cellKey, isCookingEquipment, msToTicks } from '@overcooked/shared'
import { itemFlow } from './itemFlow'
import { scheduledOrder } from './orders'
import { reachableFrom } from './regions'

// ---------------------------------------------------------------------------
// Map
// ---------------------------------------------------------------------------

/**
 * One character per tile, whitespace-separated so the file is
 * quick to write by hand.
 */
export const MAP_LEGEND: Readonly<Record<string, StationType>> = {
  W: 'wall',
  '.': 'floor',
  X: 'counter',
  C: 'board',
  A: 'stove',
  O: 'oven',
  M: 'crate',
  N: 'crate',
  U: 'crate',
  H: 'crate',
  D: 'plate_stack',
  K: 'sink',
  '#': 'bin',
  S: 'hatch',
  R: 'plate_return',
  E: 'extinguisher_rack',
}

/** Which ingredient each crate glyph dispenses. Validated against recipes. */
export const CRATE_CONTENTS: Readonly<Record<string, IngredientId>> = {
  M: 'meat',
  N: 'carrot',
  U: 'onion',
  H: 'mushroom',
}

/** Spawn glyphs are the digits `1`, `2`, `3`, ... — seat order, not identity. */
const SPAWN_GLYPH = /^[1-9]$/

/**
 * How small and how large a kitchen may be, per side.
 *
 * The bound lives here rather than in the editor's resize button because map
 * *text* is no longer something only a maintainer with a shell can produce: it
 * is drawn in a browser and posted to a server. A limit enforced by the widget
 * that happens to be in front of it is not a limit, so `parseMap` — the one
 * function the server, the CLI and the editor all go through — is where it is
 * asked.
 *
 * Three is the smallest grid with an inside: a wall ring around one tile. The
 * shipped presets run 5x5 to 10x15, so thirty-two per side is roughly ten times
 * the largest kitchen anybody has drawn and still a number a browser can paint
 * a grid of buttons for.
 */
export const MIN_MAP_SIDE = 3
export const MAX_MAP_SIDE = 32

export interface MapTile {
  cell: Cell
  type: StationType
  /** Crates only. */
  dispenses: IngredientId | null
}

/** A container this layout starts with, and the station it starts on. */
export interface ContainerPlacement {
  kind: ContainerKind
  cell: Cell
}

export interface MapContent {
  name: string
  width: number
  height: number
  /** Every cell of the grid, keyed by `cellKey`. Walls and floors included. */
  tiles: Record<string, MapTile>
  /** Spawn cells in seat order — index 0 is seat 1. */
  spawns: Cell[]
  /**
   * Which dishes this layout is solvable for, from its `// orders:` directive.
   * Empty means "whatever `recipes.json` says", which is how a hand-written
   * test fixture stays a three-line string.
   */
  orderPool: string[]
  /** The pans the round starts with, from `// container:` directives. */
  containers: ContainerPlacement[]
  /**
   * When the tickets arrive, from `// schedule:` directives — sorted by tick,
   * file order kept within a tick. Absent (never an empty list) when the map
   * has none, which means the regular cadence over the pool; absent rather
   * than empty so a map without one parses, hashes and serialises exactly as
   * it did before schedules existed.
   */
  orderSchedule?: ScheduledOrder[]
}

/**
 * Directives are comment lines, so a map file stays a picture of a kitchen.
 *
 *   // orders: fried_meat, veg_stew
 *   // container: pan @ 6,2
 *   // schedule: 0s fried_meat, veg_stew
 *   // schedule: 60s roast_platter
 *
 * They are content, not code: which dishes a layout can serve, which vessels
 * it starts with and when its tickets arrive are exactly the things that
 * differ between kitchens, and none of them should be a branch in the engine.
 */
function parseDirectives(
  rows: string[],
  name: string,
): { orderPool: string[]; containers: ContainerPlacement[]; orderSchedule: ScheduledOrder[] } {
  const orderPool: string[] = []
  const containers: ContainerPlacement[] = []
  const orderSchedule: ScheduledOrder[] = []

  for (const raw of rows) {
    const line = raw.replace(/^\/\/\s*/, '')
    const orders = /^orders\s*:(.*)$/i.exec(line)
    if (orders) {
      for (const id of orders[1].split(',')) {
        const trimmed = id.trim()
        if (trimmed) orderPool.push(trimmed)
      }
      continue
    }
    const schedule = /^schedule\s*:(.*)$/i.exec(line)
    if (schedule) {
      orderSchedule.push(...parseScheduleLine(schedule[1], name))
      continue
    }
    const container = /^container\s*:\s*(\S+)\s*@\s*(-?\d+)\s*,\s*(-?\d+)\s*$/i.exec(line)
    if (container) {
      containers.push({ kind: container[1], cell: { x: Number(container[2]), y: Number(container[3]) } })
    }
  }

  // Stable: `Array.prototype.sort` keeps equal ticks in file order, which is
  // what makes "two at 0s" two distinct, predictable ids.
  orderSchedule.sort((a, b) => a.atTick - b.atTick)
  return { orderPool, containers, orderSchedule }
}

/**
 * The time on a schedule line: seconds, written `<n>s` with no space — `0s`,
 * `12.5s`. Nothing else: `60 s`, `1m` and `-5s` are refused rather than
 * guessed at, because a schedule read wrongly is a round played to the wrong
 * timetable and nothing downstream could tell.
 */
const SCHEDULE_LINE = /^\s*(\d+(?:\.\d+)?)s\s+(\S.*)$/

/** One `// schedule:` line, after the colon, as entries in file order. */
function parseScheduleLine(rest: string, name: string): ScheduledOrder[] {
  const refuse = (why: string): never => {
    throw new ContentError(
      `Kitchen "${name}" has a schedule line "${rest.trim()}" ${why}. ` +
        'Write it as "// schedule: <seconds>s dish, dish", for example "// schedule: 60s fried_meat".',
    )
  }
  if (/^\s*\d+(?:\.\d+)?s\s*$/.test(rest)) refuse('that names no dish')
  const parsed = SCHEDULE_LINE.exec(rest)
  if (!parsed) refuse('whose time is not a number of seconds like 0s or 12.5s')
  const [, seconds, dishes] = parsed as RegExpExecArray
  const atTick = msToTicks(Number(seconds) * 1000)
  const entries: ScheduledOrder[] = []
  for (const id of dishes.split(',')) {
    const dishId = id.trim()
    if (dishId) entries.push({ atTick, dishId })
  }
  if (entries.length === 0) refuse('that names no dish')
  return entries
}

/** A tick as the seconds a schedule line writes it: `625` -> `12.5s`. */
export function scheduleSeconds(atTick: number): string {
  return `${Number(((atTick * TICK_MS) / 1000).toFixed(3))}s`
}

export class ContentError extends Error {}

/*
 * The messages `parseMap` and `validateMap` throw are read by people: the
 * editor's checklist prints them, and so does a refused save. So they name the
 * kitchen, say what is wrong in plain words, and say what to change where that
 * is not obvious. The recipe and timing checks further down are only ever read
 * by whoever maintains the content files, and keep their terser form.
 */

/** `plate_stack` -> `plate stack`, for a sentence. */
const stationWords = (type: string): string => type.replace(/_/g, ' ')

/** `extinguisher_rack` -> `an extinguisher rack`. */
const aStation = (type: string): string => {
  const words = stationWords(type)
  return `${/^[aeiou]/i.test(words) ? 'an' : 'a'} ${words}`
}

export function parseMap(text: string, name: string): MapContent {
  const all = text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
  const rows = all.filter((line) => !line.startsWith('//'))
  const { orderPool, containers, orderSchedule } = parseDirectives(
    all.filter((line) => line.startsWith('//')),
    name,
  )

  if (rows.length === 0) throw new ContentError(`Kitchen "${name}" is empty. Draw at least one row of tiles.`)

  const grid = rows.map((row) => row.split(/\s+/))
  const width = grid[0].length
  grid.forEach((row, y) => {
    if (row.length !== width) {
      throw new ContentError(
        `Kitchen "${name}": row ${y + 1} has ${row.length} tiles, but row 1 has ${width}. Every row needs the same number of tiles.`,
      )
    }
  })

  // Both sides, before a tile is read: a grid outside the bounds is refused for
  // being the wrong size rather than for whatever its first odd glyph is.
  const side = (value: number, axis: 'wide' | 'tall'): void => {
    if (value < MIN_MAP_SIDE || value > MAX_MAP_SIDE) {
      throw new ContentError(
        `Kitchen "${name}" is ${value} tiles ${axis}. Each side must be ${MIN_MAP_SIDE} to ${MAX_MAP_SIDE} tiles.`,
      )
    }
  }
  side(width, 'wide')
  side(grid.length, 'tall')

  const tiles: Record<string, MapTile> = {}
  const spawnsByIndex = new Map<number, Cell>()

  grid.forEach((row, y) => {
    row.forEach((glyph, x) => {
      const cell: Cell = { x, y }

      if (SPAWN_GLYPH.test(glyph)) {
        const seat = Number(glyph)
        if (spawnsByIndex.has(seat)) {
          throw new ContentError(`Kitchen "${name}" has more than one player ${seat} start. Use each number once.`)
        }
        spawnsByIndex.set(seat, cell)
        // A spawn stands on floor.
        tiles[cellKey(cell)] = { cell, type: 'floor', dispenses: null }
        return
      }

      const type = MAP_LEGEND[glyph]
      if (!type) {
        throw new ContentError(
          `Kitchen "${name}" has an unknown tile "${glyph}" at (${x},${y}). Use one of ${Object.keys(MAP_LEGEND).join(' ')}, or a player number.`,
        )
      }

      tiles[cellKey(cell)] = {
        cell,
        type,
        dispenses: type === 'crate' ? (CRATE_CONTENTS[glyph] ?? null) : null,
      }

      if (type === 'crate' && !CRATE_CONTENTS[glyph]) {
        throw new ContentError(`Kitchen "${name}" has a crate tile "${glyph}" that holds no known ingredient.`)
      }
    })
  })

  if (spawnsByIndex.size === 0) {
    throw new ContentError(`Kitchen "${name}" has no player start. Put a 1 on a floor tile.`)
  }

  const seats = [...spawnsByIndex.keys()].sort((a, b) => a - b)
  seats.forEach((seat, i) => {
    if (seat !== i + 1) {
      throw new ContentError(
        `Kitchen "${name}" has player starts ${seats.join(', ')}. Number them 1, 2, 3 and so on, with no gaps.`,
      )
    }
  })

  return {
    name,
    width,
    height: grid.length,
    tiles,
    spawns: seats.map((seat) => spawnsByIndex.get(seat) as Cell),
    orderPool,
    containers,
    ...(orderSchedule.length > 0 ? { orderSchedule } : {}),
  }
}

// ---------------------------------------------------------------------------
// Recipes
// ---------------------------------------------------------------------------

export interface IngredientDef {
  label: string
  raw: boolean
}

export interface TransformDef {
  station: StationType
  from: IngredientId
  to: IngredientId
  durationKey: string
}

export interface CookableDef {
  station: StationType
  /** Which container kind this row needs. A pan cannot stew. */
  container: ContainerKind
  from: IngredientId
  to: IngredientId
  toDoneKey: string
  toBurntKey: string
  /** Time constraint #4. Falls back to `burntToFireMs` when a row omits it. */
  toFireKey?: string
}

export interface ContainerDef {
  label: string
  /** How many ingredients fit at once. The shipped pan holds one, so a stew is three separate cooks onto one plate. */
  capacity: number
  /** Which static equipment this container may be set down on. */
  equipment: StationType[]
}

export interface EquipmentDef {
  label: string
  /** The station type this piece of carried equipment lives on when idle. */
  rack: StationType
}

export interface DishDef {
  id: string
  label: string
  contents: IngredientId[]
  /**
   * Extra points a matched serve of this dish pays on top of
   * `scoring.baseScorePerOrder` and the speed bonus. Absent means 0. A
   * whole, non-negative number: harder dishes pay more, never less, and the
   * log never carries a fraction of a point.
   */
  bonus?: number
}

export interface RecipeContent {
  ingredients: Record<IngredientId, IngredientDef>
  containers: Record<ContainerKind, ContainerDef>
  equipment: Record<EquipmentId, EquipmentDef>
  transforms: TransformDef[]
  cookable: CookableDef[]
  dishes: DishDef[]
  orderPool: string[]
}

// ---------------------------------------------------------------------------
// Timings
// ---------------------------------------------------------------------------

export interface ScoringContent {
  baseScorePerOrder: number
  maxSpeedBonus: number
  expiredOrderPenalty: number
  unmatchedServePenalty: number
}

export interface TimingsContent {
  roundLengthMs: number
  /**
   * The order budget, expressed as slack *on top of* what the dish costs to
   * cook rather than as an absolute window.
   *
   * A pan cooks one ingredient at a time, so a three-part dish costs three
   * cooks: any fixed "45 seconds per ticket" would be generous for fried meat
   * and unservable for a stew. `orderSlackMs` is therefore the headroom the
   * *first* ticket gets over its own dish's cook time; `orderSlackDecayMs` is
   * how much of that headroom each subsequent slot loses, so the round tightens
   * as it goes; `minOrderSlackMs` is the floor, so the last ticket of a long
   * round is still a ticket somebody can fill.
   *
   * Decay is per *slot index*, never per elapsed second, because `orderForSlot`
   * has to stay a pure function of the slot for replay to reproduce the queue.
   */
  orderSlackMs: number
  orderSlackDecayMs: number
  minOrderSlackMs: number
  orderSpawnIntervalMs: number
  maxActiveOrders: number
  firstOrderDelayMs: number
  chopMs: number
  chopVegMs: number
  panCookToDoneMs: number
  doneToBurntMs: number
  stewToDoneMs: number
  stewToBurntMs: number
  ovenBakeToDoneMs: number
  ovenBakeToBurntMs: number
  /** Time constraint #4, and the only clock the design doc calls out by name. */
  burntToFireMs: number
  extinguishMs: number
  washMs: number
  plateReturnDelayMs: number
  platesPerMap: number
  /**
   * How many cooked items one plate holds. A pan cooks one ingredient at a
   * time, so the plate is the assembly surface — and a bounded one, because an
   * unbounded plate is a pile only the bin can undo.
   */
  plateCapacity: number
  /** Lobby patience, phase 5. Both are tuning decisions, so both are data. */
  readyTimeoutMs: number
  voiceReadyGraceMs: number
  /**
   * The fewest milliseconds between two moves from an agent seat.
   *
   * The engine never reads it, because the engine does not know which seats are
   * agents. The agent paces itself to it and the transport refuses an early
   * move from an agent seat. It lives here so every log header records it.
   * Zero turns pacing off.
   */
  agentMoveIntervalMs: number
  /**
   * How fast the oracle's chefs walk: the milliseconds between two moves of
   * one chef in a solved round.
   *
   * The engine never reads it either. It is content rather than a solver
   * constant because the oracle's score is only meaningful at a stated pace —
   * a chef that steps every tick sets a ceiling no person or agent can reach —
   * and a pace written in the file is in the `contentHash` and in every
   * result. It may not be faster than `agentMoveIntervalMs`, so an AI seat
   * can always execute the oracle's tape move for move.
   */
  oracleStepMs: number
  scoring: ScoringContent
  /**
   * The latency gate. Its threshold is a tuning decision, so it lives here rather
   * than in code — settling it with Yiheng after the first playtest is a file
   * edit and a redeploy, not a patch.
   */
  latency: LatencyConfig
  /** Every duration is addressable by name so recipes can reference it. */
  [key: string]: unknown
}

const REQUIRED_TIMINGS = [
  'burntToFireMs',
  'extinguishMs',
  'roundLengthMs',
  'orderSlackMs',
  'orderSlackDecayMs',
  'minOrderSlackMs',
  'orderSpawnIntervalMs',
  'maxActiveOrders',
  'firstOrderDelayMs',
  'chopMs',
  'panCookToDoneMs',
  'doneToBurntMs',
  'washMs',
  'plateReturnDelayMs',
  'platesPerMap',
  'plateCapacity',
  'readyTimeoutMs',
  'voiceReadyGraceMs',
  'agentMoveIntervalMs',
  'oracleStepMs',
] as const

const REQUIRED_LATENCY = [
  'gateSampleCount',
  'gateSampleTimeoutMs',
  'gateMaxMedianRttMs',
  'voiceSampleIntervalMs',
] as const

const REQUIRED_SCORING = [
  'baseScorePerOrder',
  'maxSpeedBonus',
  'expiredOrderPenalty',
  'unmatchedServePenalty',
] as const

// ---------------------------------------------------------------------------
// The bundle
// ---------------------------------------------------------------------------

export interface Content {
  /** The map served by default. Also what a single-map test fixture overrides. */
  map: MapContent
  /** Every preset the loader found, keyed by name. Includes `map`. */
  maps: Record<string, MapContent>
  recipes: RecipeContent
  timings: TimingsContent
}

/**
 * The pool a given layout plays with: the dishes its schedule names, when it
 * has one (first appearance first); else its own `// orders:` line; else the
 * file's. A schedule wins over an orders line beside it — that line spawns
 * nothing, and `mapReport` says so.
 */
export function orderPoolFor(content: Content, map: MapContent = content.map): string[] {
  if (map.orderSchedule && map.orderSchedule.length > 0) {
    return [...new Set(map.orderSchedule.map((entry) => entry.dishId))]
  }
  return map.orderPool.length > 0 ? map.orderPool : content.recipes.orderPool
}

/** The map's schedule, or an empty list when it runs on the cadence. */
export const scheduleOf = (map: MapContent): ScheduledOrder[] => map.orderSchedule ?? []

/**
 * Everything wrong with a map's schedule, as sentences — empty when it has
 * none or it is sound. One implementation for the thrower (`validateMap`) and
 * the checklist (`mapReport`), so the two cannot disagree.
 *
 * Three rules, each one about the schedule being the *whole* story of when
 * tickets arrive:
 *
 *   - **known dishes.** A ticket for a dish the recipe book does not define
 *     could never be served.
 *   - **inside the round.** An entry at or after the whistle never spawns.
 *   - **never more than the rail holds.** The engine skips a ticket that finds
 *     the rail full, so whether it appears would depend on how the round went.
 *     The check assumes nothing is ever served — the most tickets that could
 *     be open at once — and settles expiries before spawns on the same tick,
 *     as the engine does. A valid schedule therefore always issues every
 *     entry, and its arrivals are a property of the map alone.
 *
 * Reads the round length, the rail and the clocks a ticket's fuse is built
 * from (`orderClockKeys`); nothing else.
 */
export function scheduleProblems(
  content: Content,
  map: MapContent,
): { known: string[]; rail: string[]; peak: number } {
  const schedule = scheduleOf(map)
  const known: string[] = []
  const rail: string[] = []
  let peak = 0
  if (schedule.length === 0) return { known, rail, peak }

  const dishes = new Set(content.recipes.dishes.map((d) => d.id))
  const roundTicks = msToTicks(content.timings.roundLengthMs)
  for (const entry of schedule) {
    if (!dishes.has(entry.dishId)) {
      known.push(`"${entry.dishId}" at ${scheduleSeconds(entry.atTick)} is not a dish the recipe book defines`)
    } else if (entry.atTick >= roundTicks) {
      known.push(
        `"${entry.dishId}" at ${scheduleSeconds(entry.atTick)} would arrive after the round ends at ${scheduleSeconds(roundTicks)}`,
      )
    }
  }
  // Fuses are only meaningful for dishes that exist.
  if (known.length > 0) return { known, rail, peak }

  const cap = content.timings.maxActiveOrders
  const orders = schedule.map((entry, index) => scheduledOrder(content, entry, index))
  const ticks = [...new Set(orders.flatMap((o) => [o.spawnedAtTick, o.expiresAtTick]))].sort((a, b) => a - b)
  let live: typeof orders = []
  for (const t of ticks) {
    live = live.filter((o) => o.expiresAtTick > t)
    live.push(...orders.filter((o) => o.spawnedAtTick === t))
    peak = Math.max(peak, live.length)
    if (live.length > cap && rail.length === 0) {
      rail.push(
        `${live.length} orders would be open at ${scheduleSeconds(t)} (tick ${t}), but the rail holds ${cap}` +
          ` — spread the schedule out so no more than ${cap} are open at once, even if nothing is served`,
      )
    }
  }
  return { known, rail, peak }
}

/**
 * The `timings.json` keys `scheduleProblems` reads: the round, the rail, the
 * slack curve, and the cook-to-done clock of every cooked item a dish names
 * (a ticket's fuse is its dish's cook time plus slack). Exactly these, so the
 * editor can be handed what checking a schedule needs and nothing more.
 */
export function orderClockKeys(recipes: RecipeContent): string[] {
  const keys = ['roundLengthMs', 'maxActiveOrders', 'orderSlackMs', 'orderSlackDecayMs', 'minOrderSlackMs']
  for (const dish of recipes.dishes) {
    for (const item of dish.contents) {
      const row = recipes.cookable.find((c) => c.to === item)
      if (row && !keys.includes(row.toDoneKey)) keys.push(row.toDoneKey)
    }
  }
  return keys
}

/** Those keys' values, for `EditorContent.orderClock`. */
export function orderClockOf(content: Content): Record<string, number> {
  const out: Record<string, number> = {}
  for (const key of orderClockKeys(content.recipes)) out[key] = content.timings[key] as number
  return out
}

/**
 * What a dish needs to exist: which crates, which transforming stations, which
 * cooking equipment and which container kinds.
 *
 * Walking the recipe DAG backwards from a dish is the only honest way to answer
 * "is this kitchen solvable", and it is why `validateContent` can refuse a map
 * that ships a roast platter with no oven rather than letting two players
 * discover it forty seconds into a round.
 */
export interface DishRequirements {
  stations: Set<StationType>
  containers: Set<ContainerKind>
  /** Raw ingredients that must come out of a crate. */
  sources: Set<IngredientId>
  /**
   * Largest number of ingredients that must share one container *at once*.
   *
   * Since the pan became a one-ingredient vessel this is the size of a single
   * cook rather than the size of the dish: each cooked item is its own panful
   * and the plate is what holds the dish together. The field stays because the
   * question is still the right one to ask of content — a row that cooked two
   * things into one would need a vessel that takes two.
   */
  containerLoad: Map<ContainerKind, number>
  /** How many cooked items end up on one plate — i.e. the dish's length. */
  plateLoad: number
}

export function dishRequirements(recipes: RecipeContent, dish: DishDef): DishRequirements {
  const out: DishRequirements = {
    stations: new Set(),
    containers: new Set(),
    sources: new Set(),
    containerLoad: new Map(),
    plateLoad: dish.contents.length,
  }

  const resolve = (target: IngredientId, seen: Set<IngredientId>): void => {
    if (seen.has(target)) {
      throw new ContentError(`recipes.json has a cycle producing "${target}"`)
    }
    const cook = recipes.cookable.find((c) => c.to === target)
    if (cook) {
      out.stations.add(cook.station)
      out.containers.add(cook.container)
      resolve(cook.from, new Set([...seen, target]))
      return
    }
    const transform = recipes.transforms.find((t) => t.to === target)
    if (transform) {
      out.stations.add(transform.station)
      resolve(transform.from, new Set([...seen, target]))
      return
    }
    out.sources.add(target)
  }

  for (const item of dish.contents) resolve(item, new Set())

  // How many ingredients have to be in one vessel at the same moment.
  //
  // This used to be the dish's whole length, because a stew was one batch. It
  // is now the size of one cook, because a stew is three: the vessel is
  // emptied onto the plate between ingredients, so a dish never asks a pan for
  // more than the row it is cooking. Summing the dish here again would refuse
  // to boot a kitchen that plays perfectly well.
  //
  // A cookable row names one `from` ingredient, so one cook is one slot; the
  // max is over the dish's cooks rather than a sum of them.
  for (const item of dish.contents) {
    const cook = recipes.cookable.find((c) => c.to === item)
    if (!cook) continue
    out.containerLoad.set(cook.container, Math.max(out.containerLoad.get(cook.container) ?? 0, 1))
  }

  return out
}

/**
 * The ingredient DAG, flattened into one row per ingredient, for the client.
 *
 * The renderer has to draw fourteen distinguishable ingredient icons
 * and is forbidden from knowing any of their names, so it is told the shape of
 * the graph instead: every ingredient's label, whether `recipes.json` calls it
 * raw, and the single row that produces it. A source ingredient — one that
 * comes out of a crate — has `from: null`, and those are the only ones the
 * renderer needs a drawn shape for; everything else is that shape plus the
 * treatments implied by the stations it passed through.
 *
 * Ordered by id so the payload is stable between boots, which keeps a diff of
 * `/api/config` readable.
 */
export function ingredientCatalogue(recipes: RecipeContent): IngredientSummary[] {
  return Object.keys(recipes.ingredients)
    .sort()
    .map((id) => {
      const def = recipes.ingredients[id]
      const cook = recipes.cookable.find((c) => c.to === id)
      const transform = cook ? undefined : recipes.transforms.find((t) => t.to === id)
      const from = cook
        ? { station: cook.station, ingredient: cook.from, container: cook.container }
        : transform
          ? { station: transform.station, ingredient: transform.from, container: null }
          : null
      return { id, label: def.label, raw: def.raw, from }
    })
}

/**
 * A dish as a recipe card: for every item that has to end up on the plate, the
 * crate it starts in, the equipment it passes through and the vessel it cooks
 * in.
 *
 * `dishRequirements` above answers "is this kitchen solvable" and collapses
 * everything into sets to do it. A ticket needs the opposite: order and
 * multiplicity, because three stewed vegetables are three icons on the card and
 * one stewed carrot is one.
 */
export function dishTicket(recipes: RecipeContent, dish: DishDef): DishRequirement {
  const items: RequiredItem[] = dish.contents.map((id) => {
    const stations: StationType[] = []
    let container: ContainerKind | null = null
    let cursor = id
    const seen = new Set<IngredientId>()

    for (;;) {
      if (seen.has(cursor)) throw new ContentError(`recipes.json has a cycle producing "${cursor}"`)
      seen.add(cursor)
      const cook = recipes.cookable.find((c) => c.to === cursor)
      if (cook) {
        stations.unshift(cook.station)
        container = cook.container
        cursor = cook.from
        continue
      }
      const transform = recipes.transforms.find((t) => t.to === cursor)
      if (transform) {
        stations.unshift(transform.station)
        cursor = transform.from
        continue
      }
      break
    }

    return { id, source: cursor, stations, container }
  })

  const containers: ContainerKind[] = []
  const stations: StationType[] = []
  for (const item of items) {
    if (item.container && !containers.includes(item.container)) containers.push(item.container)
    for (const station of item.stations) if (!stations.includes(station)) stations.push(station)
  }

  return { items, containers, stations }
}

/** Stations no round can be played without, whatever the dish is. */
const ALWAYS_REQUIRED_STATIONS: StationType[] = [
  'counter',
  'plate_stack',
  'sink',
  'bin',
  'hatch',
  'plate_return',
]

/**
 * Every map must be solvable for the dishes it ships with, and must keep the
 * two-sides-of-a-counter split that forces cooperation.
 *
 * "Solvable" here means the union of what the seats can reach covers every
 * station and container the dish's recipe DAG needs, and there is at least one
 * counter both seats can face — which is the only way anything crosses when the
 * two floor regions are disjoint.
 */
export function validateMap(content: Content, map: MapContent): void {
  const { recipes } = content
  const pool = orderPoolFor(content, map)

  const stationTypes = new Set<StationType>(Object.values(map.tiles).map((t) => t.type))
  const crateIngredients = new Set<IngredientId>(
    Object.values(map.tiles)
      .filter((t) => t.type === 'crate' && t.dispenses)
      .map((t) => t.dispenses as IngredientId),
  )

  for (const placement of map.containers) {
    const def = recipes.containers[placement.kind]
    if (!def) {
      throw new ContentError(`Kitchen "${map.name}" places an unknown container "${placement.kind}".`)
    }
    const tile = map.tiles[cellKey(placement.cell)]
    if (!tile) {
      throw new ContentError(
        `Kitchen "${map.name}" places a ${placement.kind} at (${placement.cell.x},${placement.cell.y}), which is outside the grid.`,
      )
    }
    const allowed = tile.type === 'counter' || def.equipment.includes(tile.type)
    if (!allowed) {
      throw new ContentError(
        `Kitchen "${map.name}" places a ${placement.kind} on ${aStation(tile.type)} at (${placement.cell.x},${placement.cell.y}). ` +
          `A ${placement.kind} can only start on a counter or ${def.equipment.map(stationWords).join(' or ')}.`,
      )
    }
  }
  const startingContainers = new Set<ContainerKind>(map.containers.map((c) => c.kind))

  // Anything the round cannot be played without, whatever the dish is.
  for (const required of ALWAYS_REQUIRED_STATIONS) {
    if (!stationTypes.has(required)) {
      throw new ContentError(`Kitchen "${map.name}" has no ${stationWords(required)}. Every kitchen needs one.`)
    }
  }
  // Fire is now a real outcome on every map that can cook, so every map that
  // can cook must also carry the means to put one out.
  const canCook = [...stationTypes].some((t) => isCookingEquipment(t))
  if (canCook && !stationTypes.has('extinguisher_rack')) {
    throw new ContentError(
      `Kitchen "${map.name}" can cook but has no extinguisher rack. Add one so fires can be put out.`,
    )
  }

  // The schedule, before the dishes it names are walked: an unknown dish
  // there is a schedule mistake, and is better reported as one.
  const schedule = scheduleProblems(content, map)
  const scheduleError = schedule.known[0] ?? schedule.rail[0]
  if (scheduleError) {
    throw new ContentError(`Kitchen "${map.name}" has a schedule problem: ${scheduleError}.`)
  }

  for (const dishId of pool) {
    const dish = recipes.dishes.find((d) => d.id === dishId)
    if (!dish) throw new ContentError(`Kitchen "${map.name}" orders an unknown dish "${dishId}".`)
    const need = dishRequirements(recipes, dish)

    for (const station of need.stations) {
      if (!stationTypes.has(station)) {
        throw new ContentError(`Kitchen "${map.name}" serves "${dishId}" but has no ${stationWords(station)}.`)
      }
    }
    for (const kind of need.containers) {
      if (!startingContainers.has(kind)) {
        throw new ContentError(
          `Kitchen "${map.name}" serves "${dishId}" but starts with no ${kind}. Add one with a "// container:" line.`,
        )
      }
      const capacity = recipes.containers[kind].capacity
      const load = need.containerLoad.get(kind) ?? 0
      if (load > capacity) {
        throw new ContentError(
          `Kitchen "${map.name}" serves "${dishId}", which needs ${load} ingredients in one ${kind} at once, but a ${kind} holds ${capacity}.`,
        )
      }
    }
    // The other half of the same question, now that a dish is assembled on the
    // plate rather than in the vessel: a ticket nobody can finish plating is
    // as unwinnable as one nobody can cook.
    const plateHolds = content.timings.plateCapacity
    if (need.plateLoad > plateHolds) {
      throw new ContentError(
        `Kitchen "${map.name}" serves "${dishId}", which needs ${need.plateLoad} items on one plate, but a plate holds ${plateHolds}.`,
      )
    }
    for (const source of need.sources) {
      if (!crateIngredients.has(source)) {
        throw new ContentError(`Kitchen "${map.name}" serves "${dishId}" but has no ${stationWords(source)} crate.`)
      }
    }
  }

  // The split. Two seats, two disjoint floor regions, one shared counter.
  const regions = map.spawns.map((spawn) => reachableFrom(map, spawn))
  const faceable = new Set<string>()
  for (const region of regions) for (const key of region.faced) faceable.add(key)

  // One spawn means a solo kitchen, and the handover rule below cannot apply
  // to it — there is nobody to hand anything to. Until now that meant a
  // single-region map was held to no reachability standard at all: the checks
  // above ask only whether a station *exists* somewhere on the grid, which is
  // the right question when two seats between them cover the room and the
  // wrong one when there is a single pair of legs. So a one-spawn map has to
  // prove the stronger claim — every station, crate and starting vessel each
  // of its dishes needs is faceable from the one region a lone chef stands in.
  //
  // Checked before the general "no tile any seat can face" sweep below, which
  // would otherwise report the same layout as a stray tile rather than as a
  // dish nobody can cook.
  if (regions.length === 1) {
    const reach = regions[0]
    const typesInReach = new Set<StationType>()
    const cratesInReach = new Set<IngredientId>()
    for (const key of reach.faced) {
      const tile = map.tiles[key]
      if (!tile) continue
      typesInReach.add(tile.type)
      if (tile.type === 'crate' && tile.dispenses) cratesInReach.add(tile.dispenses)
    }

    const locked = (what: string): never => {
      throw new ContentError(
        `Kitchen "${map.name}" has one player, but ${what}. A solo player could not finish the dish.`,
      )
    }

    for (const required of ALWAYS_REQUIRED_STATIONS) {
      if (!typesInReach.has(required)) locked(`the player cannot reach ${aStation(required)}`)
    }
    if (canCook && !typesInReach.has('extinguisher_rack')) {
      locked('the player cannot reach an extinguisher rack')
    }

    for (const dishId of pool) {
      const dish = recipes.dishes.find((d) => d.id === dishId) as DishDef
      const need = dishRequirements(recipes, dish)
      for (const station of need.stations) {
        if (!typesInReach.has(station)) {
          locked(`"${dishId}" needs ${aStation(station)} the player cannot reach`)
        }
      }
      for (const source of need.sources) {
        if (!cratesInReach.has(source)) {
          locked(`"${dishId}" needs ${aStation(source)} crate the player cannot reach`)
        }
      }
      for (const kind of need.containers) {
        const placed = map.containers.filter((c) => c.kind === kind)
        if (!placed.some((c) => reach.faced.has(cellKey(c.cell)))) {
          locked(`"${dishId}" needs a ${kind}, and every ${kind} starts out of the player's reach`)
        }
      }
    }
  }

  for (const [key, tile] of Object.entries(map.tiles)) {
    if (tile.type === 'floor' || tile.type === 'wall') continue
    if (!faceable.has(key)) {
      throw new ContentError(
        `Kitchen "${map.name}" has ${aStation(tile.type)} at (${tile.cell.x},${tile.cell.y}) that no player can reach.`,
      )
    }
  }

  if (regions.length >= 2) {
    const shared = [...regions[0].faced].filter((key) =>
      regions.slice(1).every((r) => r.faced.has(key)),
    )
    // Existence, not order: the boot path only asks whether *a* counter both
    // seats can face exists. Sorting them into reading order is `mapReport`'s
    // job, because it is the one that prints the list.
    if (!shared.some((key) => map.tiles[key]?.type === 'counter')) {
      throw new ContentError(
        `Kitchen "${map.name}" has no counter both players can reach, so nothing can be passed between them.`,
      )
    }
  }
}

export function validateContent(content: Content): void {
  const { map, recipes, timings } = content

  for (const key of REQUIRED_TIMINGS) {
    const value = timings[key]
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      throw new ContentError(`timings.json is missing a numeric "${key}"`)
    }
  }
  if (!timings.scoring || typeof timings.scoring !== 'object') {
    throw new ContentError('timings.json is missing the "scoring" block')
  }
  for (const key of REQUIRED_SCORING) {
    if (typeof timings.scoring[key] !== 'number') {
      throw new ContentError(`timings.json scoring is missing a numeric "${key}"`)
    }
  }

  // The order queue runs off these four, and a zero in any of them is a round
  // with no game in it. Say so at boot rather than at tick 0.
  for (const key of [
    'roundLengthMs',
    'orderSlackMs',
    'minOrderSlackMs',
    'orderSpawnIntervalMs',
    'maxActiveOrders',
  ] as const) {
    if ((timings[key] as number) <= 0) {
      throw new ContentError(`timings.json "${key}" must be greater than zero`)
    }
  }
  // A negative decay would make every ticket *more* generous than the one
  // before it, which is the curve backwards; zero is legal and means "no decay".
  if (timings.orderSlackDecayMs < 0) {
    throw new ContentError('timings.json "orderSlackDecayMs" must not be negative')
  }
  // Zero is legal and means agent seats are not paced at all.
  if (timings.agentMoveIntervalMs < 0) {
    throw new ContentError('timings.json "agentMoveIntervalMs" must not be negative')
  }
  // The oracle's pace is a claim that an AI seat could play its tape move for
  // move. The server refuses an agent move sooner than `agentMoveIntervalMs`
  // after the last one, so a faster oracle would be planning moves no seat is
  // allowed to make.
  if (timings.oracleStepMs <= 0) {
    throw new ContentError('timings.json "oracleStepMs" must be greater than zero')
  }
  if (timings.oracleStepMs < timings.agentMoveIntervalMs) {
    throw new ContentError(
      `timings.json "oracleStepMs" (${timings.oracleStepMs}) must not be faster than "agentMoveIntervalMs" ` +
        `(${timings.agentMoveIntervalMs}) — the oracle would plan moves an agent seat is refused`,
    )
  }
  // A floor above the starting slack is a decay that never happens, stated in
  // two numbers that disagree. Better caught here than puzzled over in a log.
  if (timings.minOrderSlackMs > timings.orderSlackMs) {
    throw new ContentError(
      'timings.json "minOrderSlackMs" must not exceed "orderSlackMs" — the floor cannot be above the start',
    )
  }
  if (!Array.isArray(recipes.orderPool) || recipes.orderPool.length === 0) {
    throw new ContentError('recipes.json orderPool is empty — no ticket could ever be issued')
  }

  if (!timings.latency || typeof timings.latency !== 'object') {
    throw new ContentError('timings.json is missing the "latency" block')
  }
  for (const key of REQUIRED_LATENCY) {
    const value = timings.latency[key]
    if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
      throw new ContentError(`timings.json latency is missing a positive numeric "${key}"`)
    }
  }
  if (timings.latency.gateSampleCount < 3) {
    throw new ContentError('timings.json latency.gateSampleCount must be at least 3 — a median of one or two samples is noise')
  }

  const known = new Set(Object.keys(recipes.ingredients))
  if (known.size === 0) throw new ContentError('recipes.json declares no ingredients')

  for (const t of recipes.transforms) {
    if (!known.has(t.from) || !known.has(t.to)) {
      throw new ContentError(`recipes.json transform ${t.from} -> ${t.to} names an unknown ingredient`)
    }
    if (typeof timings[t.durationKey] !== 'number') {
      throw new ContentError(`recipes.json transform ${t.from} -> ${t.to} references missing timing "${t.durationKey}"`)
    }
  }

  if (!recipes.containers || Object.keys(recipes.containers).length === 0) {
    throw new ContentError('recipes.json declares no containers — nothing could ever be cooked')
  }
  for (const [kind, def] of Object.entries(recipes.containers)) {
    if (!Number.isInteger(def.capacity) || def.capacity < 1) {
      throw new ContentError(`recipes.json container "${kind}" needs an integer capacity of at least 1`)
    }
    if (!Array.isArray(def.equipment) || def.equipment.length === 0) {
      throw new ContentError(`recipes.json container "${kind}" rests on no equipment`)
    }
    for (const station of def.equipment) {
      if (!isCookingEquipment(station)) {
        throw new ContentError(
          `recipes.json container "${kind}" names "${station}", which is not cooking equipment`,
        )
      }
    }
  }

  for (const [id, def] of Object.entries(recipes.equipment ?? {})) {
    if (!def.rack) throw new ContentError(`recipes.json equipment "${id}" declares no rack station`)
  }

  for (const c of recipes.cookable) {
    if (!known.has(c.from) || !known.has(c.to)) {
      throw new ContentError(`recipes.json cookable ${c.from} -> ${c.to} names an unknown ingredient`)
    }
    if (!isCookingEquipment(c.station)) {
      throw new ContentError(
        `recipes.json cookable ${c.from} -> ${c.to} names "${c.station}", which is not cooking equipment`,
      )
    }
    if (!recipes.containers[c.container]) {
      throw new ContentError(
        `recipes.json cookable ${c.from} -> ${c.to} needs container "${c.container}", which is not declared`,
      )
    }
    // All three of the design doc's cooking clocks, per row. The third is time
    // constraint #4 and is what turns a forgotten pan into a fire.
    for (const key of [c.toDoneKey, c.toBurntKey, c.toFireKey ?? 'burntToFireMs']) {
      if (typeof timings[key] !== 'number') {
        throw new ContentError(`recipes.json cookable ${c.from} -> ${c.to} references missing timing "${key}"`)
      }
    }
  }

  // A plate that holds nothing is a kitchen that serves nothing, and one that
  // holds fewer items than the longest dish is a ticket nobody can finish.
  // Both are boot-time facts, so they are boot-time errors.
  if (!Number.isInteger(timings.plateCapacity) || timings.plateCapacity < 1) {
    throw new ContentError('timings.json needs an integer plateCapacity of at least 1')
  }

  const dishIds = new Set<string>()
  for (const dish of recipes.dishes) {
    if (dishIds.has(dish.id)) throw new ContentError(`recipes.json declares dish "${dish.id}" twice`)
    dishIds.add(dish.id)
    if (dish.contents.length === 0) {
      throw new ContentError(`recipes.json dish "${dish.id}" has no contents`)
    }
    if (dish.contents.length > timings.plateCapacity) {
      throw new ContentError(
        `recipes.json dish "${dish.id}" is ${dish.contents.length} items, but timings.json plateCapacity is ${timings.plateCapacity}`,
      )
    }
    for (const item of dish.contents) {
      if (!known.has(item)) {
        throw new ContentError(`recipes.json dish "${dish.id}" contains unknown ingredient "${item}"`)
      }
    }
    // A bonus is added to a serve's score as-is, so a fraction would put
    // floating-point noise into the log and a negative one would make a harder
    // dish a worse choice than an unmatched plate. Absent is 0.
    if (dish.bonus !== undefined && (!Number.isInteger(dish.bonus) || dish.bonus < 0)) {
      throw new ContentError(
        `recipes.json dish "${dish.id}" has bonus ${JSON.stringify(dish.bonus)} — it must be a whole number of points, 0 or more`,
      )
    }
  }

  for (const dishId of recipes.orderPool) {
    if (!dishIds.has(dishId)) {
      throw new ContentError(`recipes.json orderPool names unknown dish "${dishId}"`)
    }
  }

  for (const candidate of Object.values(content.maps ?? { [map.name]: map })) {
    for (const tile of Object.values(candidate.tiles)) {
      if (tile.type === 'crate' && (!tile.dispenses || !known.has(tile.dispenses))) {
        throw new ContentError(
          `map "${candidate.name}" crate at (${tile.cell.x},${tile.cell.y}) dispenses "${tile.dispenses}", which recipes.json does not define`,
        )
      }
    }
    for (const dishId of candidate.orderPool) {
      if (!dishIds.has(dishId)) {
        throw new ContentError(`map "${candidate.name}" orders unknown dish "${dishId}"`)
      }
    }
  }
}

/**
 * Which preset is served when nobody asks for one. A map is a data file and the
 * choice between them is an environment variable, so shipping a different
 * kitchen is a restart rather than a build.
 */
export const DEFAULT_MAP_NAME = 'medium-7x10'

/**
 * The kitchen a solo game opens on.
 *
 * Named here rather than in `rooms/routes.ts` because which layout seats one
 * person is a property of the content, not of the endpoint that asks for it —
 * and because `validateMap` has to hold this one to a different standard.
 */
export const SOLO_MAP_NAME = 'solo-6x8'

/** The map a room asked for, falling back to the default rather than failing. */
export function mapNamed(content: Content, name: string | null | undefined): MapContent {
  if (!name) return content.map
  return content.maps[name] ?? content.map
}

// ---------------------------------------------------------------------------
// Drawing a map, and saying what is wrong with it
//
// `validateMap` above is the authority on whether a layout may be played, and
// it is deliberately a thrower: the first thing wrong with a kitchen is the
// reason the process must not boot, and there is nothing useful the server could
// do with the second. That is exactly the wrong shape for somebody *editing* a
// kitchen, who wants every failing rule at once and wants the passing ones named
// too, so the report reads as a checklist rather than as a stack trace.
//
// So this section adds no rules that stop a boot. It walks the same recipe DAG
// through `dishRequirements` and the same floor regions through `reachableFrom` —
// the two functions `validateMap` itself walks — and reports what it finds per
// rule instead of throwing on the first one. A boot-blocking check here that
// disagreed with the validator would be a bug in this file; the CLI and the
// browser editor run both, and the validator is what decides the exit code.
//
// It does report one rule the boot path does not enforce — whether the seats
// stand in disjoint regions — and that rule is marked `advisory` so that nothing
// downstream can mistake it for a refusal. See `MapCheck.advisory`.
// ---------------------------------------------------------------------------

/** What a cell with no tile at all draws as. A parsed map has none; a half-edited grid does. */
const EMPTY_GLYPH = '.'

/**
 * The glyph that draws each station type, inverted from the legend so there is
 * still exactly one table. First glyph wins, which is why a crate's glyph comes
 * from what it dispenses rather than from here — four glyphs mean `crate`.
 *
 * `Partial`, and deliberately not cast to a total record. The legend is the only
 * source of entries, so a station type added to `StationType` without a glyph
 * beside it is simply absent here — and a total type would let that compile,
 * make `serialiseMap` write the literal `"undefined"` into the grid, and hand
 * `/api/editor/content` a preset the parser then rejects. Missing is a throw.
 */
const GLYPH_FOR_STATION: Readonly<Partial<Record<StationType, string>>> = (() => {
  const byType: Partial<Record<StationType, string>> = {}
  for (const [glyph, type] of Object.entries(MAP_LEGEND)) if (!(type in byType)) byType[type] = glyph
  return byType
})()

/** The glyph for one parsed tile, crates included. */
export function glyphForTile(tile: MapTile): string {
  if (tile.type === 'crate' && tile.dispenses) {
    const glyph = Object.entries(CRATE_CONTENTS).find(([, id]) => id === tile.dispenses)?.[0]
    if (glyph) return glyph
  }
  const glyph = GLYPH_FOR_STATION[tile.type]
  if (glyph === undefined) {
    throw new ContentError(
      `no glyph in MAP_LEGEND draws a "${tile.type}" — a station type was added without a legend entry`,
    )
  }
  return glyph
}

/**
 * A parsed map, back as the grid of glyphs it was written as.
 *
 * The inverse of `parseMap`, and round-trip tested against every shipped preset:
 * `parseMap(serialiseMap(m), m.name)` is `m`. Two consumers need it and neither
 * has the file — the CLI draws what it parsed rather than echoing bytes it never
 * read, which is what makes `render` evidence that the parse agrees with the
 * picture, and the browser editor is handed maps over HTTP as parsed content
 * because the server keeps no copy of the text.
 */
export function mapGlyphs(map: MapContent): string[][] {
  const rows: string[][] = []
  for (let y = 0; y < map.height; y += 1) {
    const row: string[] = []
    for (let x = 0; x < map.width; x += 1) {
      const seat = map.spawns.findIndex((cell) => cell.x === x && cell.y === y)
      if (seat >= 0) {
        row.push(String(seat + 1))
        continue
      }
      const tile = map.tiles[cellKey({ x, y })]
      row.push(tile ? glyphForTile(tile) : EMPTY_GLYPH)
    }
    rows.push(row)
  }
  return rows
}

/** A map file, directives and all, from parsed content. */
export function serialiseMap(map: MapContent): string {
  const lines: string[] = []
  if (map.orderPool.length > 0) lines.push(`// orders: ${map.orderPool.join(', ')}`)
  // One line per arrival time, dishes in their (already sorted) order, so the
  // file parses back to the same entries with the same ids.
  const schedule = scheduleOf(map)
  for (let i = 0; i < schedule.length; ) {
    const at = schedule[i].atTick
    const dishes: string[] = []
    while (i < schedule.length && schedule[i].atTick === at) dishes.push(schedule[i++].dishId)
    lines.push(`// schedule: ${scheduleSeconds(at)} ${dishes.join(', ')}`)
  }
  for (const placement of map.containers) {
    lines.push(`// container: ${placement.kind} @ ${placement.cell.x},${placement.cell.y}`)
  }
  for (const row of mapGlyphs(map)) lines.push(row.join(' '))
  return `${lines.join('\n')}\n`
}

// ---------------------------------------------------------------------------
// What `GET /api/editor/content` carries
//
// The wire shape of that endpoint, declared once, here, because the two ends of
// it are in different packages and a browser cannot import the server. Written
// down twice — once in `server/src/content/editorRoutes.ts` and once in
// `client/src/editor/draft.ts` — it would typecheck on both sides through a
// rename of `plateCapacity` or the addition of a required field, and break only
// in the tab, at runtime, for whoever opened `/editor` next. It lives in the
// engine rather than in `@overcooked/shared` because `RecipeContent` does: the
// recipe book is the thing the editor validates against, and moving it would
// leave the shape referring to a type its own package could not see.
// ---------------------------------------------------------------------------

/** A preset as the editor opens it: the file text, rebuilt from parsed content. */
export interface EditorMap {
  name: string
  /** `serialiseMap`'s output — the bytes `npm run map -- validate` would read. */
  text: string
  seats: number
}

/** What the server hands over so a browser can validate a kitchen it just drew. */
export interface EditorContent {
  /**
   * The whole recipe book. The DAG is what "is this dish solvable" is a question
   * about, so a validator without it can only check glyphs.
   */
  recipes: RecipeContent
  /** The one rule `validateMap` reads out of the knobs. Not a clock. */
  plateCapacity: number
  /**
   * The order clocks a `// schedule:` is checked against, and only those
   * (`orderClockKeys`): the round length, the rail, the slack curve and each
   * dish's cook-to-done time. A schedule that overflows the rail is refused
   * at boot, so the editor has to be able to say so while it is drawn. These
   * are the inputs to a ticket's fuse, which every `ORDER_SPAWNED` already
   * hands a browser resolved into ticks; the renderer never reads them, so no
   * bar in a round can be drawn from them.
   */
  orderClock: Record<string, number>
  maps: EditorMap[]
  defaultMap: string
}

/** One rule, and whether this layout keeps it. */
export interface MapCheck {
  /** What the rule is called, short enough to read down a column. */
  rule: string
  ok: boolean
  /** The sentence a person acts on — which dish, which station, which region. */
  detail: string
  /**
   * True when breaking this rule is advice rather than a refusal.
   *
   * `validateMap` is the authority on whether a kitchen boots, and every rule
   * below that it enforces is reported with `advisory: false` — a disagreement
   * between the two would be a bug in this file. But the report also names one
   * rule the boot path deliberately does not enforce: "the seats stand in
   * disjoint regions", which describes a kitchen where neither chef needs the
   * other. That kitchen boots, plays and records a round; it is just not the
   * experiment. Whoever reads a report has to be able to tell those apart,
   * because a tool that says "this would not boot" about a map that boots is
   * wrong in the one sentence it exists to say.
   */
  advisory: boolean
  /**
   * True for a rule that refuses a *save* but not a boot: the item-flow proof.
   *
   * A kitchen nobody can carry a dish through is not one an editor or an
   * admin should be allowed to store. But maps already in the bucket were
   * stored before the proof existed, and refusing to load them at boot would
   * drop them from the index (`MapStore.load`) — a saved afternoon gone
   * because a rule moved. So the boot path never reads these rows; the save
   * paths do. Absent means false.
   */
  saveOnly?: boolean
}

/**
 * The checks that decide whether a layout boots — `validateMap`'s rules,
 * exactly. Advice and save-only rules are left out.
 */
export const bootBlocking = (checks: readonly MapCheck[]): MapCheck[] =>
  checks.filter((check) => !check.advisory && !check.saveOnly)

/**
 * The checks that decide whether a layout may be saved: every boot rule plus
 * the save-only ones (item flow). What the editor's Save button, admin save,
 * `npm run map -- validate` and `npm run oracle -- validate` answer to.
 */
export const saveBlocking = (checks: readonly MapCheck[]): MapCheck[] =>
  checks.filter((check) => !check.advisory)

/** The item-flow rule's name, as the checklist and the CLIs print it. */
export const ITEM_FLOW_RULE = 'every dish can be carried crate to hatch'
/** The plate-recycling advice's name. */
export const PLATE_RECYCLE_RULE = 'dirty plates can be washed and reused'

/** What one seat's floor region can reach. */
export interface RegionReach {
  /** Seat number, 1-based, as the spawn digit wrote it. */
  seat: number
  floorCells: number
  /** Station types this region can stand in front of. */
  stations: StationType[]
  /** Ingredients this region can take out of a crate. */
  crates: IngredientId[]
  /** Container kinds this region can pick a starting vessel up from. */
  containers: ContainerKind[]
}

export interface MapReport {
  name: string
  width: number
  height: number
  /** The dishes this layout is held to — its own pool, or the recipe file's. */
  pool: string[]
  regions: RegionReach[]
  /** Counters every region can face. The only way anything crosses the split. */
  sharedCounters: Cell[]
  checks: MapCheck[]
}

/** Human phrasing for "which seats can reach this". */
const seatList = (seats: number[]): string =>
  seats.length === 1 ? `region ${seats[0]}` : `regions ${seats.join(',')}`

/**
 * Every rule `validateMap` enforces, reported rather than thrown — plus one it
 * does not, marked `advisory` so the difference survives being printed.
 *
 * Ordered the way a map designer thinks: what the kitchen is, whether each dish
 * can be cooked in it, whether the seats can reach each other, and only then the
 * glyph-level facts. The interesting checks are the first two — a layout whose
 * glyphs all parse and whose stew has no mushroom crate is a round two people
 * lose for reasons they cannot see.
 */
export function mapReport(content: Content, map: MapContent): MapReport {
  const { recipes } = content
  const pool = orderPoolFor(content, map)
  const checks: MapCheck[] = []

  /**
   * A rule `validateMap` throws on. Failing one here is a server that refuses to
   * boot, so the two must agree — see `mapReport.test.ts`, which asserts both
   * verdicts on every fixture rather than only this one.
   */
  const enforced = (check: Omit<MapCheck, 'advisory'>): void => {
    checks.push({ ...check, advisory: false })
  }
  /** A rule only this report names. Worth saying; not a reason a kitchen fails to start. */
  const advice = (check: Omit<MapCheck, 'advisory'>): void => {
    checks.push({ ...check, advisory: true })
  }

  const reaches = map.spawns.map((spawn) => reachableFrom(map, spawn))
  const facilities = reaches.map((reach) => {
    const stations = new Set<StationType>()
    const crates = new Set<IngredientId>()
    const containers = new Set<ContainerKind>()
    for (const key of reach.faced) {
      const tile = map.tiles[key]
      if (!tile) continue
      stations.add(tile.type)
      if (tile.type === 'crate' && tile.dispenses) crates.add(tile.dispenses)
    }
    for (const placement of map.containers) {
      if (reach.faced.has(cellKey(placement.cell))) containers.add(placement.kind)
    }
    return { stations, crates, containers }
  })

  const regions: RegionReach[] = facilities.map((f, i) => ({
    seat: i + 1,
    floorCells: reaches[i].floor.size,
    stations: [...f.stations].sort(),
    crates: [...f.crates].sort(),
    containers: [...f.containers].sort(),
  }))

  // Which seats can reach a thing. A duo kitchen is solvable as a team, so the
  // failing case is "no seat at all"; naming the seats that *can* is what tells
  // a designer whether the work is shared or piled on one side.
  const seatsWith = <T>(pick: (f: (typeof facilities)[number]) => Set<T>, want: T): number[] =>
    facilities.flatMap((f, i) => (pick(f).has(want) ? [i + 1] : []))

  // -- the kitchen itself ---------------------------------------------------

  enforced({
    rule: 'seats',
    ok: map.spawns.length > 0,
    detail:
      map.spawns.length === 0
        ? 'no spawn digit — nobody can stand in this kitchen'
        : `${map.spawns.length} seat(s) on ${map.width}x${map.height}, ` +
          regions.map((r) => `region ${r.seat}: ${r.floorCells} floor cells`).join('; '),
  })

  for (const required of ALWAYS_REQUIRED_STATIONS) {
    const seats = seatsWith((f) => f.stations, required)
    enforced({
      rule: `has a ${required}`,
      ok: seats.length > 0,
      detail: seats.length > 0 ? `reachable from ${seatList(seats)}` : `no seat can face a ${required}`,
    })
  }

  const canCook = Object.values(map.tiles).some((t) => isCookingEquipment(t.type))
  if (canCook) {
    const seats = seatsWith((f) => f.stations, 'extinguisher_rack' as StationType)
    enforced({
      rule: 'extinguisher for the cooking equipment',
      ok: seats.length > 0,
      detail:
        seats.length > 0
          ? `reachable from ${seatList(seats)}`
          : 'this kitchen can cook and cannot put a fire out — a fire would end the round',
    })
  }

  // -- per dish, walked through the recipe DAG ------------------------------

  const nobody = map.spawns.length === 1 ? 'region 1' : 'any region'

  for (const dishId of pool) {
    const dish = recipes.dishes.find((d) => d.id === dishId)
    if (!dish) {
      enforced({ rule: `${dishId} is solvable`, ok: false, detail: `recipes.json declares no "${dishId}"` })
      continue
    }

    let need: DishRequirements
    try {
      need = dishRequirements(recipes, dish)
    } catch (err) {
      enforced({ rule: `${dishId} is solvable`, ok: false, detail: (err as Error).message })
      continue
    }

    const missing: string[] = []
    const covered: string[] = []

    for (const station of [...need.stations].sort()) {
      const seats = seatsWith((f) => f.stations, station)
      if (seats.length === 0) missing.push(`no ${station} reachable from ${nobody}`)
      else covered.push(`${station} (${seatList(seats)})`)
    }
    for (const source of [...need.sources].sort()) {
      const seats = seatsWith((f) => f.crates, source)
      if (seats.length === 0) missing.push(`no ${source} crate reachable from ${nobody}`)
      else covered.push(`${source} crate (${seatList(seats)})`)
    }
    for (const kind of [...need.containers].sort()) {
      const seats = seatsWith((f) => f.containers, kind)
      if (seats.length === 0) {
        missing.push(
          map.containers.some((c) => c.kind === kind)
            ? `every ${kind} starts where no seat can reach it`
            : `this layout starts with no ${kind}`,
        )
      } else {
        covered.push(`${kind} (${seatList(seats)})`)
      }
      const capacity = recipes.containers[kind]?.capacity ?? 0
      const load = need.containerLoad.get(kind) ?? 0
      if (load > capacity) missing.push(`one cook needs ${load} ingredients in a ${kind}, which holds ${capacity}`)
    }
    if (need.plateLoad > content.timings.plateCapacity) {
      missing.push(`${need.plateLoad} items on one plate, which holds ${content.timings.plateCapacity}`)
    }

    enforced({
      rule: `${dishId} is solvable`,
      ok: missing.length === 0,
      detail: missing.length > 0 ? missing.join('; ') : covered.join(', '),
    })
  }

  // -- when the tickets arrive, if the map says -----------------------------
  //
  // Only asked of a map with a `// schedule:`. Every dish it names is already a
  // "<dish> is solvable" row above, because the schedule *is* the pool then.

  const schedule = scheduleOf(map)
  if (schedule.length > 0) {
    const problems = scheduleProblems(content, map)
    const byTick = new Map<number, number>()
    for (const entry of schedule) byTick.set(entry.atTick, (byTick.get(entry.atTick) ?? 0) + 1)
    enforced({
      rule: 'the schedule fits the round',
      ok: problems.known.length === 0,
      detail:
        problems.known.length > 0
          ? problems.known.join('; ')
          : `${schedule.length} order(s): ` +
            [...byTick].map(([tick, n]) => `${n} at ${scheduleSeconds(tick)}`).join(', '),
    })
    // Meaningless until every entry is a real dish inside the round, so it
    // waits for the row above rather than reporting a second failure.
    if (problems.known.length === 0) {
      enforced({
        rule: 'the schedule fits the rail',
        ok: problems.rail.length === 0,
        detail:
          problems.rail.length > 0
            ? problems.rail.join('; ')
            : `at most ${problems.peak} open at once even if nothing is served; the rail holds ` +
              `${content.timings.maxActiveOrders}, so every scheduled order arrives`,
      })
    }
    advice({
      rule: 'the schedule decides the orders',
      ok: map.orderPool.length === 0,
      detail:
        map.orderPool.length === 0
          ? `only the scheduled orders arrive: ${orderPoolFor(content, map).join(', ')}`
          : `the "// orders: ${map.orderPool.join(', ')}" line is ignored — with a schedule, only the` +
            ' scheduled orders arrive; delete the orders line to say so',
    })
  }

  // -- whether each dish can actually be carried through ------------------
  //
  // The rows above ask whether what a dish needs exists within some seat's
  // reach. This one walks it: crate to board to heat, a plate from the stack
  // past each heat to a hatch, every change of hands over something both
  // chefs can reach (`itemFlow.ts`). It refuses a save and never a boot — see
  // `MapCheck.saveOnly`.

  const flow = itemFlow(content, map, pool)
  checks.push({
    rule: ITEM_FLOW_RULE,
    ok: flow.ok,
    detail: flow.ok
      ? flow.dishes
          .map((d) =>
            d.forcedHandoffs === null
              ? d.dish
              : `${d.dish} (${d.forcedHandoffs === 0 ? 'no hand-off needed' : `at least ${d.forcedHandoffs} hand-off${d.forcedHandoffs === 1 ? '' : 's'}`})`,
          )
          .join(', ')
      : flow.dishes
          .filter((d) => !d.ok)
          .map((d) => d.problem)
          .join('; '),
    advisory: false,
    saveOnly: true,
  })
  advice({ rule: PLATE_RECYCLE_RULE, ok: flow.recycles, detail: flow.recycleDetail })

  // -- the split, which is what makes a two-seat kitchen two-seat -----------

  const shared = [...(reaches[0]?.faced ?? [])].filter((key) =>
    reaches.slice(1).every((r) => r.faced.has(key)),
  )
  // Reading order — `reachableFrom` walks a queue, so its `faced` set comes out
  // in whatever order the BFS happened to touch things, and a list of counters
  // that jumps around the column is harder to check against the drawing than one
  // that goes down it.
  const sharedCounters = shared
    .filter((key) => map.tiles[key]?.type === 'counter')
    .sort((a, b) => {
      const [ax, ay] = a.split(',').map(Number)
      const [bx, by] = b.split(',').map(Number)
      return ay - by || ax - bx
    })

  if (map.spawns.length >= 2) {
    const disjoint = reaches.every((reach, i) =>
      reaches.every((other, j) => i === j || [...reach.floor].every((key) => !other.floor.has(key))),
    )
    // Advice, not a refusal. `validateMap` asks for a shared counter and says
    // nothing about shared floor, so a one-room two-seat kitchen boots — it is
    // simply a kitchen in which neither chef needs the other, which is worth
    // telling whoever is drawing it and is not worth failing CI over.
    advice({
      rule: 'the seats stand in disjoint regions',
      ok: disjoint,
      detail: disjoint
        ? 'no seat can walk into another seat\'s half — everything has to cross a counter'
        : 'the seats share floor, so either chef can do the whole dish alone',
    })
    // The other half of the same sentence, and only worth asking when the
    // answer above was "they share floor".
    //
    // "Either chef can do the whole dish alone" is true of every one-room
    // kitchen and is not the end of the description. A shared floor a chef can
    // always walk round is a kitchen where the other chef is scenery: nobody
    // ever has to give way, `yield` is a verb with no occasion, and the
    // deconfliction the AI-AI condition exists to measure never happens. A
    // shared floor with a choke point in it is the opposite — the two of them
    // have to take turns at a named cell, and which one gave way is a decision
    // somebody made and the tape can be read for.
    //
    // A choke is an articulation point of the floor graph: a cell one chef can
    // stand on that cuts the other off from somewhere. That is exactly the
    // condition under which `navigate.ts` answers `blocked: 'partner'` rather
    // than quietly routing around, so this rule reports whether the layout can
    // produce the event the condition is about.
    if (!disjoint) {
      const chokes = chokePoints(map)
      advice({
        rule: 'the shared floor has a choke point',
        ok: chokes.length > 0,
        detail:
          chokes.length > 0
            ? `${chokes.length} cell(s) one chef can block the other at: ` +
              chokes.map((cell) => `(${cell.x},${cell.y})`).join(' ') +
              ' — the seats have to take turns'
            : 'every floor cell can be walked round, so neither chef can ever be' +
              ' in the other\'s way — nobody will ever need to give way',
      })
    }
    enforced({
      rule: 'the regions share a counter',
      ok: sharedCounters.length > 0,
      detail:
        sharedCounters.length > 0
          ? `${sharedCounters.length} shared counter(s) at ` +
            sharedCounters.map((key) => `(${key})`).join(' ')
          : `regions ${regions.map((r) => r.seat).join(' and ')} share no counter` +
            ' — the seats cannot hand anything across',
    })
  }

  // -- the glyph-level facts, last ------------------------------------------

  const stranded = Object.entries(map.tiles)
    .filter(([key, tile]) => tile.type !== 'floor' && tile.type !== 'wall' && !facedBySomeSeat(reaches, key))
    .map(([, tile]) => `${tile.type} at (${tile.cell.x},${tile.cell.y})`)
  enforced({
    rule: 'every station faces a seat',
    ok: stranded.length === 0,
    detail: stranded.length === 0 ? 'nothing is walled off' : `no seat can face ${stranded.join(', ')}`,
  })

  const badPlacements: string[] = []
  for (const placement of map.containers) {
    const def = recipes.containers[placement.kind]
    const tile = map.tiles[cellKey(placement.cell)]
    if (!def) badPlacements.push(`"${placement.kind}" is not a container recipes.json declares`)
    else if (!tile) badPlacements.push(`a ${placement.kind} at (${placement.cell.x},${placement.cell.y}) is off the grid`)
    else if (!(tile.type === 'counter' || def.equipment.includes(tile.type))) {
      badPlacements.push(
        `a ${placement.kind} on a ${tile.type} at (${placement.cell.x},${placement.cell.y}); ` +
          `it may only rest on a counter or ${def.equipment.join('/')}`,
      )
    }
  }
  enforced({
    rule: 'starting containers rest somewhere legal',
    ok: badPlacements.length === 0,
    detail:
      badPlacements.length === 0
        ? map.containers.length === 0
          ? 'this layout starts with none'
          : map.containers.map((c) => `${c.kind} @ ${c.cell.x},${c.cell.y}`).join(', ')
        : badPlacements.join('; '),
  })

  return {
    name: map.name,
    width: map.width,
    height: map.height,
    pool,
    regions,
    sharedCounters: sharedCounters.map((key) => {
      const [x, y] = key.split(',').map(Number)
      return { x, y }
    }),
    checks,
  }
}

/**
 * Floor cells a chef can stand on to cut the kitchen in two.
 *
 * Articulation points of the floor graph, found the obvious way — take each
 * cell out and ask whether what is left is still one piece. A kitchen is tens
 * of floor cells, so the quadratic walk costs nothing and is worth the clarity
 * over a Hopcroft–Tarjan low-link pass nobody would read again.
 *
 * Pure geometry: it says nothing about who is standing where in a round, only
 * about which cells *could* block. A layout with none of them is a layout where
 * two chefs can always walk round each other.
 */
export function chokePoints(map: MapContent): Cell[] {
  const floor = Object.values(map.tiles).filter((t) => t.type === 'floor')
  if (floor.length <= 2) return []
  const open = new Set(floor.map((t) => cellKey(t.cell)))

  /**
   * How many pieces the floor is in with `without` taken out.
   *
   * Counted rather than asked as a yes/no, because a kitchen whose floor was
   * already in two pieces — a disjoint two-seat map — would answer "not
   * connected" about every cell in it. The question is whether removing this one
   * *adds* a piece.
   */
  const pieces = (without: string): number => {
    const seen = new Set<string>([without])
    let count = 0
    for (const tile of floor) {
      if (seen.has(cellKey(tile.cell))) continue
      count += 1
      seen.add(cellKey(tile.cell))
      const queue: Cell[] = [tile.cell]
      while (queue.length > 0) {
        const cell = queue.shift() as Cell
        for (const v of Object.values(DIRECTION_VECTORS)) {
          const next = { x: cell.x + v.x, y: cell.y + v.y }
          const key = cellKey(next)
          if (seen.has(key) || !open.has(key)) continue
          seen.add(key)
          queue.push(next)
        }
      }
    }
    return count
  }

  const whole = pieces('')
  return floor
    .filter((t) => pieces(cellKey(t.cell)) > whole)
    .map((t) => t.cell)
    .sort((a, b) => a.y - b.y || a.x - b.x)
}

/** Whether any region can stand in front of this cell. */
function facedBySomeSeat(reaches: Array<{ faced: Set<string> }>, key: string): boolean {
  return reaches.some((reach) => reach.faced.has(key))
}
